import { useState }        from 'react'
import type { ConnectedRepo } from '@/types'

function LockIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  )
}

function RepoIcon() {
  return (
    <svg className="w-4 h-4 text-forge-ash" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>
  )
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins  = Math.floor(diff / 60_000)
  if (mins < 1)   return 'just now'
  if (mins < 60)  return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30)  return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

interface RepoCardProps {
  repo:     ConnectedRepo
  onSelect: () => void
  onRemove: () => Promise<void>
}

export function RepoCard({ repo, onSelect, onRemove }: RepoCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isRemoving, setIsRemoving]       = useState(false)

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setIsRemoving(true)
    try {
      await onRemove()
    } finally {
      setIsRemoving(false)
      setConfirmDelete(false)
    }
  }

  const cancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirmDelete(false)
  }

  return (
    <div
      className="group relative bg-forge-obsidian border border-forge-border rounded-xl p-5
                 hover:border-forge-ember/50 hover:bg-forge-obsidian/80
                 transition-all duration-200 cursor-pointer animate-fade-in"
      onClick={onSelect}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {repo.avatar_url ? (
            <img
              src={repo.avatar_url}
              alt={repo.owner}
              className="w-7 h-7 rounded-full flex-shrink-0 ring-1 ring-forge-border"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-forge-basalt flex-shrink-0 flex items-center justify-center ring-1 ring-forge-border">
              <RepoIcon />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs text-forge-ash truncate">{repo.owner}</p>
            <p className="text-sm font-semibold text-forge-primary truncate group-hover:text-forge-gold transition-colors">
              {repo.display_name ?? repo.name}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {repo.is_private && (
            <span className="flex items-center gap-1 text-[10px] text-forge-ash border border-forge-border rounded px-1.5 py-0.5 uppercase tracking-wider">
              <LockIcon /> Private
            </span>
          )}
          <span className="text-[10px] text-forge-ash border border-forge-border/50 rounded px-1.5 py-0.5 font-mono">
            {repo.default_branch}
          </span>
        </div>
      </div>

      {/* Description */}
      {repo.description && (
        <p className="text-xs text-forge-ash leading-relaxed mb-3 line-clamp-2">
          {repo.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-forge-border/50">
        <p className="text-[11px] text-forge-ash">
          {repo.last_accessed_at
            ? `Accessed ${timeAgo(repo.last_accessed_at)}`
            : `Added ${timeAgo(repo.added_at)}`}
        </p>

        {/* Delete control */}
        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
          {confirmDelete ? (
            <>
              <button
                onClick={cancelDelete}
                className="text-[11px] text-forge-ash hover:text-forge-primary transition-colors uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                onClick={handleRemove}
                disabled={isRemoving}
                className="text-[11px] text-forge-heat-critical hover:text-forge-molten transition-colors uppercase tracking-wider"
              >
                {isRemoving ? 'Removing…' : 'Confirm'}
              </button>
            </>
          ) : (
            <button
              onClick={handleRemove}
              className="text-forge-border hover:text-forge-heat-critical opacity-0 group-hover:opacity-100 transition-all"
              title="Remove repository"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* Active indicator */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-0 group-hover:h-full bg-forge-ember rounded-r transition-all duration-300" />
    </div>
  )
}
